import { useCallback, useState } from 'react';
import { stanNotify } from '@/lib/stanNotify';
import { isAxiosError } from 'axios';
import { useUpdateProfileImage, useUploadProfileImage } from '../queries';

interface UseProfileAvatarUploadReturn {
    handleAvatarCropped: (params: { croppedBlob: Blob; croppedDataUrl: string }) => Promise<void>;
    isUploading: boolean;
}

/**
 * Hook for handling profile avatar upload workflow
 * Orchestrates file upload to S3 and profile image update
 */
export function useProfileAvatarUpload(pickedFileName?: string): UseProfileAvatarUploadReturn {
    const [isUploading, setIsUploading] = useState(false);
    const { mutateAsync: uploadToS3 } = useUploadProfileImage();
    const { mutateAsync: updateProfileImage } = useUpdateProfileImage();

    const handleAvatarCropped = useCallback(
        async ({ croppedBlob }: { croppedBlob: Blob; croppedDataUrl: string }) => {
            // Store the file name before clearing state
            const fileName = pickedFileName?.replace(/\.[^.]+$/, '.png') || 'profile-image.png';

            try {
                setIsUploading(true);

                // Convert blob to File for upload (use PNG to preserve transparency from circular mask)
                const file = new File([croppedBlob], fileName, {
                    type: 'image/png',
                });

                // Upload file to S3
                const { url } = await uploadToS3(file);

                // Update profile image on server
                await updateProfileImage(url);

                // Show success notification
                stanNotify({
                    type: 'success',
                    title: 'Profile Updated',
                    text: 'Your profile picture has been updated successfully!',
                    duration: 3000,
                });
            } catch (error) {
                let errorMessage = 'Failed to upload avatar. Please try again.';

                if (error instanceof Error && (error as Error & { isFileSizeError?: boolean }).isFileSizeError) {
                    errorMessage = error.message;
                } else if (isAxiosError(error)) {
                    errorMessage = error.response?.data?.message || error.message;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                stanNotify({
                    type: 'error',
                    title: 'Update Failed',
                    text: errorMessage,
                    duration: 5000,
                });

                throw error; // Re-throw to allow component to handle cleanup
            } finally {
                setIsUploading(false);
            }
        },
        [pickedFileName, uploadToS3, updateProfileImage]
    );

    return {
        handleAvatarCropped,
        isUploading,
    };
}
